import { useCallback, useEffect, useState, useMemo } from "react";
import "./IssuesTable.css";
import { getIssues } from "../utils/auth";
const IssuesTable = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedError, setSelectedError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // 🔹 Fetch Issues
const fetchIssues = useCallback(async () => {
  try {
    setLoading(true);
    setError("");
    const res = await getIssues();
    setIssues(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    setError("Failed to fetch issues");
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // 🔍 Parse details safely
  const parsedIssues = useMemo(() => {
    return issues.map((item) => {
      try {
        const parsed = JSON.parse(item.details);
        return {
          ...item,
          errorMessage: parsed?.error?.message || "No message",
          stack: parsed?.error?.stack || "",
          url: parsed?.url || "",
          lastNode: parsed?.lastNodeExecuted || "",
        };
      } catch {
        return {
          ...item,
          errorMessage: "Invalid JSON",
          stack: "",
          url: "",
          lastNode: "",
        };
      }
    });
  }, [issues]);

  // 🔍 Search
  const filtered = useMemo(() => {
    return parsedIssues.filter((item) =>
      String(item.workflow || "").toLowerCase().includes(search.toLowerCase()) ||
      item.errorMessage.toLowerCase().includes(search.toLowerCase())
    );
  }, [parsedIssues, search]);
const sortedByLatest = useMemo(() => {
  return [...filtered].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
}, [filtered]);
  // 📄 Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = sortedByLatest.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (date) =>
    date ? new Date(date).toLocaleString("en-IN") : "";

  return (
    <main className="dashboard-page">
      <div className="dashboard-container">
        <header className="dashboard-header">
          <div>
            <p className="dashboard-kicker">Monitoring</p>
            <h1 className="dashboard-title">Workflow Issues</h1>
            <p className="dashboard-subtitle">
              Review automation failures, inspect error details, and open linked executions.
            </p>
          </div>
          <button className="btn btn-outline-primary" onClick={fetchIssues}>
            Refresh
          </button>
        </header>

      <section className="dashboard-panel">
      <div className="dashboard-toolbar">
        <input
          className="form-control w-auto"
          placeholder="Search issues..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && (
        <div className="dashboard-loading">
          <div className="spinner-border text-danger"></div>
          <p className="mt-2">Loading issues...</p>
        </div>
      )}

      {!loading && error && (
        <div className="dashboard-error">{error}</div>
      )}

      {!loading && paginated.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle dashboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Workflow</th>
                  <th>Error</th>
                  <th>Last Node</th>
                  <th>Execution</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginated.map((item, i) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                    <td className="fw-semibold">{item.workflow}</td>

                    <td className="text-danger">
                      {item.errorMessage}
                    </td>

                    <td>{item.lastNode}</td>

                    <td>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          Open
                        </a>
                      )}
                    </td>

                    <td>{formatDate(item.createdAt)}</td>

                    <td>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setSelectedError(item)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dashboard-pagination">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`btn ${
                  currentPage === i + 1
                    ? "btn-danger"
                    : "btn-outline-danger"
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}

      {!loading && filtered.length === 0 && (
        <div className="dashboard-empty">No issues found.</div>
      )}
      </section>

      {selectedError && (
        <div className="custom-modal">
          <div className="custom-modal-content">

            <div className="d-flex justify-content-between mb-2">
              <h6>Error Details</h6>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedError(null)}
              >
                Close
              </button>
            </div>

            <div>
              <p><strong>Workflow:</strong> {selectedError.workflow}</p>
              <p className="text-danger">
                <strong>Error:</strong> {selectedError.errorMessage}
              </p>

              <pre className="bg-dark text-white p-3 rounded small">
                {selectedError.stack}
              </pre>
            </div>
          </div>
        </div>
      )}
      </div>
    </main>
  );
};

export default IssuesTable;
